import { Template } from "@components/Template";
import { themeContext } from "@contexts/Theme";
import { Image } from "@kobalte/core";
import { playerContext } from "@layouts/App/context/Player";
import { useNavigate } from "@solidjs/router";
import { appThemes } from "@src/app/styles/themes";
import { formatPlayTime } from "@src/app/utils";
import { Show, type VoidComponent, createMemo, useContext } from "solid-js";
import {
	CIRCLE_OFFSET,
	CurrentTime,
	IMAGE_WIDTH,
	ProgressHandle,
	ProgressHandleStatic,
	ProgressHandleWrapper,
	ProgressWrapper,
	TrackImageCircle,
	TrackImageWrapper,
	TrackLength,
} from "./styled";

const RadialTrackControl: VoidComponent = () => {
	const { theme } = useContext(themeContext);
	const { player, handleSetProgress } = useContext(playerContext);
	const navigate = useNavigate();

	const onProgressMouseDown = (e: MouseEvent & { currentTarget: HTMLDivElement }) => {
		const bounds = e.currentTarget.getBoundingClientRect();
		const x = e.clientX - bounds.left;
		const y = e.clientY - bounds.top;

		const r = IMAGE_WIDTH / 2 - CIRCLE_OFFSET;
		const dist = Math.sqrt((x - r) ** 2 + (y - r) ** 2);

		if (dist >= r - 8 && dist <= r + 8) {
			const distFromStart = Math.sqrt((x - r) ** 2 + (y - 0) ** 2);

			const rad = Math.acos((r ** 2 + dist ** 2 - distFromStart ** 2) / (2 * r * dist));
			let deg = (rad * (180 * Math.PI)) / 10;
			deg = x < r ? 180 + (180 - deg) : deg;
			handleSetProgress(deg / 360);
		} else navigate("/queue");
	};

	const trackLength = createMemo(() => {
		const track = player.queue[player.track];
		if (track && track.type !== "RADIO") return track.length ?? 0;
		return 0;
	});

	const getProgress = () => {
		const l = trackLength();
		const elapsed = player.time < l ? player.time : l;
		return Number.isNaN(elapsed / l) ? 0 : elapsed / l;
	};

	return (
		<TrackImageWrapper>
			<TrackImageCircle>
				<Image.Img src={player.queue[player.track]?.photo} />
				<Image.Fallback>
					<Show when={player.loading} fallback={<img src="/logo_no_text.png" alt="voxtox" />}>
						<Template w="100%" h="100%" borderRadius="50%" />
					</Show>
				</Image.Fallback>
			</TrackImageCircle>

			<ProgressHandleWrapper>
				<Show when={player.feed.type !== "RADIO"}>
					<CurrentTime>{formatPlayTime(player.time, { s: true })}</CurrentTime>
					<TrackLength>
						-
						{formatPlayTime(trackLength() - player.time >= 0 ? trackLength() - player.time : 0, {
							s: true,
						})}
					</TrackLength>
				</Show>
			</ProgressHandleWrapper>
			<ProgressHandleWrapper>
				<ProgressWrapper style={{ "stroke-dashoffset": 0 }} height={IMAGE_WIDTH} width={IMAGE_WIDTH}>
					<circle
						cx={IMAGE_WIDTH / 2}
						cy={IMAGE_WIDTH / 2}
						r={IMAGE_WIDTH / 2 - CIRCLE_OFFSET}
						stroke={`${appThemes[theme()].colors.accentTertiary}20`}
						stroke-width="6"
						fill="#0000"
					/>
				</ProgressWrapper>
			</ProgressHandleWrapper>
			<ProgressHandleWrapper>
				<Show when={!player.loading}>
					<ProgressWrapper
						height={IMAGE_WIDTH}
						width={IMAGE_WIDTH}
						style={{
							"stroke-dashoffset": ((1 - getProgress()) * 2 * Math.PI * (IMAGE_WIDTH / 2 - CIRCLE_OFFSET)).toString(),
						}}
						loading={player.loading}
					>
						<circle
							cx={IMAGE_WIDTH / 2}
							cy={IMAGE_WIDTH / 2}
							r={IMAGE_WIDTH / 2 - CIRCLE_OFFSET}
							stroke={appThemes[theme()].colors.accentPrimary}
							stroke-width="6"
							fill="#0000"
						/>
					</ProgressWrapper>
				</Show>
			</ProgressHandleWrapper>
			<ProgressHandleWrapper>
				<Show
					when={player.loading}
					fallback={
						<ProgressHandle
							initial={{ transform: "rotate(90deg)" }}
							animate={{
								transform: `rotate(${getProgress() * 360 + 90}deg)`,
							}}
							transition={{ duration: player.loading ? 0 : 0.3 }}
						/>
					}
				>
					<ProgressHandleStatic />
				</Show>
			</ProgressHandleWrapper>
			<ProgressHandleWrapper onMouseDown={onProgressMouseDown} />
		</TrackImageWrapper>
	);
};
export default RadialTrackControl;
